import { getWidgetToken } from '@/atoms/integrationAuthAtoms';
import { getDomains } from '@/helpers/getDomains';
import { getOnPremApiBaseUrl } from '@/tauri/onprem/serverConfig';
import { isTauri, tauriStripeRedirectUrl } from '@/tauri/utils';
import { getClientVersion } from '@/utils/appVersion';
import { isOnPrem } from '@/utils/buildProfile';
import {
  AZURE_DEV_ENDPOINT,
  DEV_ENDPOINT,
  LOCALHOST_ENDPOINT,
  STAGING_ENDPOINT,
} from '@/utils/endpoints';
import { X_CLIENT_ID } from '@/utils/http/clientIdentityHeaders';
import { attachConfirmedDesktopOfflineRequestShortCircuit } from '@/utils/http/desktopOfflineNetwork';
import {
  getAuthContextForHttpClient,
  getAuthTokenForHttpClient,
} from '@/utils/http/getAuthTokenForHttpClient';
import { stripOnPremOptionalMetadataHeaders } from '@/utils/http/onPremOptionalMetadataHeaders';
import { rewriteOnPremPatchToPutAlias } from '@/utils/http/onPremPutAliasRoutes';
import {
  handleScribeHttpRejectedErrorNonCacheAuth,
  handleScribeHttpRejectedErrorWithRetry,
} from '@/utils/http/scribeHttpErrorHandler';
import { applyTracingHeaders } from '@/utils/http/tracing/tracingHeaders';
import { getTempUserId } from '@/utils/tempUser';
import { createScribeHttpClients, setScribeHttpClients } from '@heidi/api/client';
import {
  ENV_NAME,
  getEnvironment,
  type Region as ApiConfigRegion,
} from '@heidi/api/config';
import { Period } from '@heidi/commerce/contracts';
import { ONBOARDING_PAYMENT_CHOSEN } from '@heidi/growth/contracts';
import type { Region } from '@heidi/schema/ml-scribe';
import { StripeFreeTrialDays, SubscriptionTier } from '@heidi/schema/ml-scribe';
import { getWorkspaceId, HEIDI_WORKSPACE_ID_HEADER } from '@heidi/workspace/data';

export { resetLogoutCooldown } from '@/utils/http/scribeHttpErrorHandler';

export const getStripeCallbackRedirect = (showReverseModal: boolean = false) => {
  if (!showReverseModal) {
    return stripeCallbackRedirect;
  }
  return `${
    isTauri() ? tauriStripeRedirectUrl() : process.env.NEXT_PUBLIC_STRIPE_REDIRECT_URL
  }/scribe/stripe/success?showReverseModal=${showReverseModal}`;
};

export const getStripeReturnRedirect = () => {
  return isTauri()
    ? tauriStripeRedirectUrl()
    : (process.env.NEXT_PUBLIC_STRIPE_REDIRECT_URL ?? '');
};

export const stripeCallbackRedirect = `${
  isTauri() ? tauriStripeRedirectUrl() : process.env.NEXT_PUBLIC_STRIPE_REDIRECT_URL
}/scribe/stripe/success`;

export const getPaymentRedirectParams = (paymentChosen: ONBOARDING_PAYMENT_CHOSEN) => {
  switch (paymentChosen) {
    case ONBOARDING_PAYMENT_CHOSEN.PRO_TRIAL:
      return {
        fe_endpoint: stripeCallbackRedirect,
        fe_return_endpoint: getStripeReturnRedirect(),
        plan_period: Period.ONE_MONTH,
        tier: SubscriptionTier.PRO,
        free_trial_days: StripeFreeTrialDays.Value14,
      };
    case ONBOARDING_PAYMENT_CHOSEN.PRO_TRIAL_YEARLY:
      return {
        fe_endpoint: stripeCallbackRedirect,
        fe_return_endpoint: getStripeReturnRedirect(),
        plan_period: Period.ONE_YEAR,
        tier: SubscriptionTier.PRO,
        free_trial_days: StripeFreeTrialDays.Value14,
      };
    case ONBOARDING_PAYMENT_CHOSEN.PRO_PLUS_TRIAL:
      return {
        fe_endpoint: stripeCallbackRedirect,
        fe_return_endpoint: getStripeReturnRedirect(),
        plan_period: Period.ONE_MONTH,
        tier: SubscriptionTier.PRO_PLUS,
        free_trial_days: StripeFreeTrialDays.Value30,
      };
    case ONBOARDING_PAYMENT_CHOSEN.PRO_PLUS_TRIAL_YEARLY:
      return {
        fe_endpoint: stripeCallbackRedirect,
        fe_return_endpoint: getStripeReturnRedirect(),
        plan_period: Period.ONE_YEAR,
        tier: SubscriptionTier.PRO_PLUS,
        free_trial_days: StripeFreeTrialDays.Value30,
      };
    default:
      return null;
  }
};

const defaultScribeDomainMap: Partial<Record<ENV_NAME, string>> = {
  [ENV_NAME.LOCALHOST]: LOCALHOST_ENDPOINT,
  [ENV_NAME.DEV]: DEV_ENDPOINT,
  [ENV_NAME.AZURE_DEV]: AZURE_DEV_ENDPOINT,
  [ENV_NAME.STAGING]: STAGING_ENDPOINT,
  [ENV_NAME.PROD]: 'https://consultant.second-services.com',
};

export const getDefaultScribeDomain = (): string => {
  if (isOnPrem && typeof window !== 'undefined') {
    // Tauri desktop: the WebView origin is an internal app origin, not the
    // hospital server — use the user-configured server URL instead.
    if (isTauri()) {
      const serverUrl = getOnPremApiBaseUrl();
      if (serverUrl) return serverUrl;
    }
    return window.location.origin;
  }
  if (process.env.NEXT_PUBLIC_API_BASE_URL) {
    return process.env.NEXT_PUBLIC_API_BASE_URL;
  }
  return defaultScribeDomainMap[getEnvironment()] ?? DEV_ENDPOINT;
};

export const defaultScribeDomain = defaultScribeDomainMap;

export {
  applyXClientHeaders,
  getXClientHeaders,
  X_CLIENT_ID,
} from '@/utils/http/clientIdentityHeaders';

export { buildScribeSseHeaders } from '@/utils/http/scribeSseHeaders';

export { getWorkspaceIdHeader, HEIDI_WORKSPACE_ID_HEADER } from '@heidi/workspace/data';

const scribeHttpClients = createScribeHttpClients({
  getDefaultScribeDomain,
  // Token resolution — all branches documented in getAuthTokenForHttpClient.
  getAuthToken: () => getAuthTokenForHttpClient(),
  getAuthContext: () => getAuthContextForHttpClient(),
  getWorkspaceId,
  getTempUserId,
  getWidgetToken,
  getDomains: (region?: ApiConfigRegion) => getDomains(region as Region | undefined),
  getEnvironment,
  getClientId: () => X_CLIENT_ID,
  getClientVersion,
  workspaceIdHeaderName: HEIDI_WORKSPACE_ID_HEADER,
  applyTracingHeaders,
  // Tier 1 — four cache-authed instances plus identityBrokerHttp.
  // identityBroker sends the main bearer token on authenticated endpoints
  // (CIBA status/enroll/delete, Apple-migration ticket); its 401s get
  // refresh-retry + cleanup.
  onResponseError: (error, retry) => handleScribeHttpRejectedErrorWithRetry(error, retry),
  // Tier 2 — openApi only (widget-token, not cache-authed). A 401 here does
  // NOT represent main-session death; propagated WITHOUT firing
  // `debouncedExpiryLogout` and WITHOUT retry.
  onResponseErrorTier2: handleScribeHttpRejectedErrorNonCacheAuth,
});

[
  scribeHttpClients.scribeHttp,
  scribeHttpClients.identityBrokerHttp,
  scribeHttpClients.identityBrokerPublicHttp,
  scribeHttpClients.scribeHttpV2,
  scribeHttpClients.scribeHttpV2_1,
  scribeHttpClients.scribeHttpV2_2,
  scribeHttpClients.openApiHttp,
  scribeHttpClients.linkShareHttp,
].forEach(attachConfirmedDesktopOfflineRequestShortCircuit);

// On-prem desktop CORS adaptation (MOD-1490), installed on every shared
// client. The interceptor swaps PATCH for its registered PUT alias; the
// request transform strips optional metadata headers — transforms run after
// request interceptors, so this also removes headers added by an interceptor
// from a compiled @heidi/api build. Both helpers no-op outside the on-prem
// desktop runtime, and `isOnPrem` is build-time so cloud bundles drop the
// whole block.
if (isOnPrem) {
  [
    scribeHttpClients.scribeHttp,
    scribeHttpClients.identityBrokerHttp,
    scribeHttpClients.identityBrokerPublicHttp,
    scribeHttpClients.scribeHttpV2,
    scribeHttpClients.scribeHttpV2_1,
    scribeHttpClients.scribeHttpV2_2,
    scribeHttpClients.openApiHttp,
    scribeHttpClients.linkShareHttp,
  ].forEach((client) => {
    client.interceptors.request.use(rewriteOnPremPatchToPutAlias);
    const transforms = client.defaults.transformRequest;
    client.defaults.transformRequest = [
      stripOnPremOptionalMetadataHeaders,
      ...(Array.isArray(transforms) ? transforms : transforms ? [transforms] : []),
    ];
  });
}

// Hand the fully-configured instances (interceptors attached above) to the
// @heidi/api registry so domain `data/api` layers consume the SAME singletons
// (Wave 2 · E5, ARCFE-805). Per-app: the widget's wiring sets its own.
setScribeHttpClients(scribeHttpClients);

export const baseURL = scribeHttpClients.baseURL;
export const baseV1IdentityBrokerUrl = scribeHttpClients.baseV1IdentityBrokerUrl;
export const baseV2URL = scribeHttpClients.baseV2URL;
export const baseV2_1URL = scribeHttpClients.baseV2_1URL;
export const baseV2_2URL = scribeHttpClients.baseV2_2URL;
export const openApiBaseUrl = scribeHttpClients.openApiBaseUrl;
export const linkShareBaseUrl = scribeHttpClients.linkShareBaseUrl;
export const getScribeBaseUrlFromV2 = scribeHttpClients.getScribeBaseUrlFromV2;

export const scribeHttp = scribeHttpClients.scribeHttp;
export const identityBrokerHttp = scribeHttpClients.identityBrokerHttp;
export const identityBrokerPublicHttp = scribeHttpClients.identityBrokerPublicHttp;
export const scribeHttpV2 = scribeHttpClients.scribeHttpV2;
export const scribeHttpV2_1 = scribeHttpClients.scribeHttpV2_1;
export const scribeHttpV2_2 = scribeHttpClients.scribeHttpV2_2;
export const openApiHttp = scribeHttpClients.openApiHttp;
export const linkShareHttp = scribeHttpClients.linkShareHttp;
